import { useCallback, useEffect, useState } from 'react';
import { CalendarDays, CheckCircle2, Clock, Loader2, MapPin, MessageSquare, Target, Video } from 'lucide-react';
import { sessionHubService, HubBefore } from '../../services/sessionHub.service';

const MAX = 1000;

const day = (iso: string) =>
  new Date(`${iso}T12:00:00Z`).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Europe/London' });

function Card({ icon: Icon, title, children }: { icon: React.ElementType; title: string; children: React.ReactNode }) {
  return (
    <section className="bg-white border border-[#e2e8f0] rounded-2xl p-5 flex flex-col gap-3">
      <p className="text-[11px] font-bold text-primary tracking-widest uppercase flex items-center gap-1.5"><Icon size={13} />{title}</p>
      {children}
    </section>
  );
}

const field = 'w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none resize-none';
const primary = 'flex items-center justify-center gap-2 rounded-xl h-10 px-5 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm disabled:opacity-60';
const secondary = 'flex items-center justify-center gap-2 rounded-xl h-10 px-4 border border-slate-200 bg-white text-slate-600 text-sm font-bold hover:bg-slate-50 shadow-sm transition-colors disabled:opacity-60';

function Objective({ link, sessionId, objective, onChanged }: { link: string; sessionId: string; objective: NonNullable<HubBefore['objective']>; onChanged: () => void }) {
  const [suggesting, setSuggesting] = useState(false);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const send = async (response: 'confirm' | 'suggest') => {
    setBusy(true);
    setError(null);
    try {
      await sessionHubService.respondToObjective(link, sessionId, response, response === 'suggest' ? text : undefined);
      setSuggesting(false);
      setText('');
      onChanged();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card icon={Target} title="Objective">
      <p className="text-sm text-slate-700 whitespace-pre-line leading-relaxed">{objective.text}</p>
      {objective.confirmed && (
        <p className="text-xs font-semibold text-green-600 flex items-center gap-1.5"><CheckCircle2 size={14} />{objective.status ? 'You confirmed this objective' : 'Confirmed by your sponsor'}</p>
      )}
      {objective.status === 'change_suggested' && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
          You suggested a change: “{objective.suggestion}”. The facilitator will update the objective.
        </p>
      )}
      {objective.canRespond && !suggesting && (
        <div className="flex flex-wrap gap-2">
          {!objective.confirmed && <button type="button" disabled={busy} onClick={() => send('confirm')} className={primary}><CheckCircle2 size={16} /> Confirm</button>}
          <button type="button" disabled={busy} onClick={() => setSuggesting(true)} className={secondary}>Suggest a change</button>
        </div>
      )}
      {suggesting && (
        <div className="flex flex-col gap-2">
          <label htmlFor="objective-suggestion" className="text-sm font-semibold text-navy">What should change?</label>
          <textarea id="objective-suggestion" rows={3} maxLength={MAX} value={text} onChange={(e) => setText(e.target.value)} className={field} />
          <div className="flex flex-wrap gap-2">
            <button type="button" disabled={busy || !text.trim()} onClick={() => send('suggest')} className={primary}>Send suggestion</button>
            <button type="button" disabled={busy} onClick={() => { setSuggesting(false); setText(''); }} className={secondary}>Cancel</button>
          </div>
        </div>
      )}
      {error && <p className="text-xs text-red-700">{error}</p>}
    </Card>
  );
}

function Hope({ link, sessionId, hope }: { link: string; sessionId: string; hope: NonNullable<HubBefore['hope']> }) {
  const [text, setText] = useState(hope.text ?? '');
  const [saved, setSaved] = useState(hope.text ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await sessionHubService.saveHope(link, sessionId, text.trim() ? text : null);
      setSaved(r.text ?? '');
      setText(r.text ?? '');
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card icon={MessageSquare} title="What would make this session worthwhile for you?">
      {hope.canEdit ? (
        <>
          <textarea aria-label="What would make this session worthwhile for you?" rows={4} maxLength={MAX} value={text} onChange={(e) => setText(e.target.value)}
            placeholder="A sentence or two is plenty. Only the Version 1 team running the session sees this." className={field} />
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs text-slate-400">{text.length}/{MAX}</p>
            <button type="button" disabled={busy || text.trim() === saved.trim()} onClick={save} className={primary}>
              {busy && <Loader2 size={16} className="animate-spin" />} {saved ? 'Update' : 'Save'}
            </button>
          </div>
        </>
      ) : (
        <p className="text-sm text-slate-600 whitespace-pre-line">{saved || 'You did not add an answer before the session.'}</p>
      )}
      {error && <p className="text-xs text-red-700">{error}</p>}
    </Card>
  );
}

/** Before the session: details, agenda, getting there, objective and the worthwhile question. */
export function SessionBefore({ link, sessionId }: { link: string; sessionId: string }) {
  const [view, setView] = useState<HubBefore | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    sessionHubService.before(link, sessionId).then(setView).catch((e) => setError(e.message));
  }, [link, sessionId]);
  useEffect(() => { load(); }, [load]);

  if (error) return <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3">{error}</p>;
  if (!view) return <div className="flex justify-center items-center py-12 text-[#64748b]"><Loader2 size={22} className="animate-spin" /></div>;

  const { session, gettingThere: g, agenda, objective, hope } = view;
  const hasTravel = g.address || g.arrivalInstructions || g.accessNotes || g.teamsJoinNote;

  return (
    <div className="flex flex-col gap-4">
      <Card icon={CalendarDays} title="Your session">
        {session.days.length === 0 ? (
          <p className="text-sm text-slate-500">The date will appear here once it is confirmed.</p>
        ) : (
          <ul className="flex flex-col gap-1">
            {session.days.map((d) => (
              <li key={d.date} className="text-sm text-navy font-semibold flex flex-wrap items-center gap-2">
                {day(d.date)}
                {d.startTime && d.endTime && <span className="text-slate-500 font-normal flex items-center gap-1"><Clock size={13} />{d.startTime} to {d.endTime}</span>}
              </li>
            ))}
          </ul>
        )}
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
          {session.roomName && <div className="flex gap-2"><dt className="text-slate-400">Where</dt><dd className="text-slate-700">{session.roomName}</dd></div>}
          {session.leadFacilitator && <div className="flex gap-2"><dt className="text-slate-400">Facilitator</dt><dd className="text-slate-700">{session.leadFacilitator}</dd></div>}
          {session.mode && <div className="flex gap-2"><dt className="text-slate-400">You are</dt><dd className="text-slate-700">{session.mode === 'in_person' ? 'Attending in person' : 'Joining on Teams'}</dd></div>}
        </dl>
      </Card>

      {agenda.length > 0 && (
        <Card icon={Clock} title="Agenda">
          {agenda.map((d) => (
            <div key={d.date} className="flex flex-col gap-1.5">
              {agenda.length > 1 && <p className="text-sm font-semibold text-navy">{day(d.date)}</p>}
              <ol className="flex flex-col divide-y divide-[#e2e8f0]">
                {d.items.map((i, n) => (
                  <li key={n} className="py-2 flex gap-4 text-sm">
                    <span className="w-14 flex-shrink-0 font-semibold text-slate-500 tabular-nums">{i.time}</span>
                    <span className="flex-1">
                      <span className="text-slate-700">{i.title}</span>
                      {i.note && <span className="block text-xs text-slate-400 mt-0.5">{i.note}</span>}
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          ))}
        </Card>
      )}

      {hasTravel && (
        <Card icon={g.teamsJoinNote && !g.address ? Video : MapPin} title="Getting there">
          {g.address && <p className="text-sm text-slate-700 whitespace-pre-line">{g.address}</p>}
          {g.arrivalInstructions && <p className="text-sm text-slate-600 whitespace-pre-line">{g.arrivalInstructions}</p>}
          {g.accessNotes && <p className="text-sm text-slate-600 whitespace-pre-line">{g.accessNotes}</p>}
          {g.teamsJoinNote && (
            <div className="flex flex-col gap-1">
              <p className="text-sm font-semibold text-navy flex items-center gap-1.5"><Video size={14} />Joining on Teams</p>
              <p className="text-sm text-slate-600 whitespace-pre-line">{g.teamsJoinNote}</p>
            </div>
          )}
        </Card>
      )}

      {objective && <Objective link={link} sessionId={sessionId} objective={objective} onChanged={load} />}
      {hope && <Hope link={link} sessionId={sessionId} hope={hope} />}
    </div>
  );
}
